import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SUBURBS, findSuburb, suburbLabel } from '../data/vicSuburbs';
import { geocodeSuburb } from '../utils/api';

/**
 * SuburbField — a suburb picker that looks like CustomSelect but doesn't
 * throw away what the supervisor types.
 *
 *   value     SUBURBS entry { s, p, la, lo } (or a looked-up entry with custom: true), or null
 *   onChange  (entry | null) => void   — always an object with la/lo, never a bare string
 *   address   optional street address; sent with the typed text to the geocoder so
 *             "12 Harbour Esplanade" + "Dockland" still resolves
 *   label, placeholder, disabled
 *
 * Behaviour
 *   · type → filtered list (prefix matches first, then contains), arrow/Enter/Esc keys
 *   · pick a row → onChange(entry)
 *   · typed text that matches nothing → a "Look up …" row at the bottom (and Enter does the same):
 *       /api/super/geocode?q=<address, text, VIC>  → { suburb, postcode, la, lo }
 *       matched back to SUBURBS via findSuburb when possible, otherwise kept as a
 *       custom entry so Job Suburb / Postcode / Lat / Lng and the km estimate still get written
 *   · blur with unmatched text → text stays, field marked "not matched — look it up or pick from the list"
 */
const MAX_ROWS = 12;

const rank = (q) => {
  const l = q.trim().toLowerCase();
  if (!l) return SUBURBS.slice(0, MAX_ROWS);
  const pre = [], has = [];
  for (const x of SUBURBS) {
    const s = x.s.toLowerCase();
    if (s.startsWith(l) || x.p.startsWith(l)) pre.push(x);
    else if (s.includes(l)) has.push(x);
    if (pre.length >= MAX_ROWS) break;
  }
  return [...pre, ...has].slice(0, MAX_ROWS);
};

const SuburbField = ({ value, onChange, address = '', label = 'Job suburb', placeholder = 'Start typing a suburb', disabled = false }) => {
  const [text, setText] = useState(value ? suburbLabel(value) : '');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');          // status line under the field
  const wrap = useRef(null);
  const inputRef = useRef(null);

  // Keep the text in step when the parent SETS a value (picking a pipeline job, a lookup result,
  // the slot's own suburb). Deliberately does nothing when the value goes null: that is the parent
  // echoing back our own onChange(null) from onInput, and re-running setText would wipe the very
  // character the user just typed over the pre-filled suburb.
  useEffect(() => {
    if (!value) return;
    setText(suburbLabel(value));
    setNote(value.custom ? `Looked up: ${value.display || suburbLabel(value)}` : '');
  }, [value]);

  useEffect(() => {
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const rows = useMemo(() => rank(text), [text]);
  const exact = useMemo(() => findSuburb(text), [text]);
  const unmatched = text.trim() && !exact;

  const choose = (entry) => {
    onChange(entry);
    setText(suburbLabel(entry));
    setNote(entry.custom ? `Looked up: ${entry.display || suburbLabel(entry)} — check it before saving` : '');
    setOpen(false);
  };

  const lookup = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setNote('Looking up…');
    try {
      const r = await geocodeSuburb(q, address);
      if (!r || r.status !== 'ok') { setNote(r?.message || 'No match — pick the nearest suburb from the list'); return; }
      const hit = findSuburb(`${r.suburb} ${r.postcode}`) || findSuburb(r.suburb);
      choose(hit || { s: r.suburb, p: r.postcode || '', la: r.la, lo: r.lo, km: null, custom: true, display: r.display });
    } catch (e) {
      setNote(e.message || 'Lookup failed');
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (!open && ['ArrowDown', 'ArrowUp'].includes(e.key)) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, rows.length - (unmatched ? 0 : 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[hi]) choose(rows[hi]);
      else if (unmatched) lookup();
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const onBlur = () => {
    // don't snap back — leave the text and say what's needed
    if (unmatched && !busy) setNote('Not in the list — press Look up, or pick a suburb');
  };

  const onInput = (v) => {
    setText(v); setHi(0); setOpen(true);
    const hit = findSuburb(v);
    if (hit) { onChange(hit); setNote(''); }
    else if (value) { onChange(null); }            // parent knows the field is unresolved
    if (!v.trim()) setNote('');
  };

  return (
    <div className={`sf-wrap ${unmatched ? 'sf-unmatched' : ''}`} ref={wrap}>
      {label && <span className="ca-label">{label}</span>}
      <div className="sf-row">
        <input
          ref={inputRef}
          className="sf-input"
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKey}
          role="combobox"
          aria-expanded={open}
        />
        <button type="button" className="sf-lookup" disabled={disabled || busy || !text.trim()} onClick={lookup} title="Find this suburb with the address">
          {busy ? '…' : 'Look up'}
        </button>
      </div>

      {open && !disabled && (rows.length > 0 || unmatched) && (
        <ul className="sf-list" role="listbox">
          {rows.map((x, i) => (
            <li key={`${x.s}-${x.p}`} role="option" aria-selected={i === hi}
                className={`sf-opt ${i === hi ? 'sf-hi' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); choose(x); }}
                onMouseEnter={() => setHi(i)}>
              <span className="sf-name">{x.s}</span>
              <span className="sf-meta">{x.p}{x.km != null ? ` · ${x.km} km from Docklands` : ''}</span>
            </li>
          ))}
          {unmatched && (
            <li role="option" aria-selected={hi === rows.length}
                className={`sf-opt sf-lookup-row ${hi === rows.length ? 'sf-hi' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); lookup(); }}
                onMouseEnter={() => setHi(rows.length)}>
              <span className="sf-name">Look up “{text.trim()}”</span>
              <span className="sf-meta">{address ? `with ${address}` : 'via address search'}</span>
            </li>
          )}
        </ul>
      )}

      {note && <div className={`sf-note ${value?.custom ? 'sf-note-ok' : ''}`}>{note}</div>}
    </div>
  );
};

export default SuburbField;